// The game simulation. One World per arena: it runs in the browser for solo play and inside the Arena
// Durable Object for online play, so it never touches the DOM, timers or globals and never calls
// Math.random directly (opts.rng). Ported from the prototype's update() and helpers. Hosts steer humans
// with setInput, read the public arrays to build a Scene or snapshots, and drain SimEvents for sounds,
// toasts and point awards.
import {
  BOOST_DOT_MASS, BOOST_MIN_MASS, BOOST_SPEED, BOT_NAMES, DIFF, EJECT_INTERVAL, EVENTS, EVENT_TYPES,
  MAX_CELLS, MAX_TRAIL_DOTS, MILESTONES, PELLETS, PELLET_COLORS, POWERUPS, POWERUP_TYPES, RUSH_COLOR,
  SKINS, SKIN_BY_ID, START_MASS, VIRUSES, WORLD, rad, speedOf,
} from './config';
import type { Difficulty, DifficultyId, EventType, PowerUpType, SkinId } from './config';
import type {
  ActiveEvent, Cell, Pellet, PelletKind, Player, PlayerInput, PowerUp, SimEvent, Virus, WorldApi, WorldOptions,
} from './types';
import { GOLDEN_SKILL, biggestCell, botSkill, botThink, newBrain, totalMass } from './bots';
import { shuffle, type Rng } from './rng';

export { centerOf, totalMass } from './bots';

const FIRST_PLACE_COOLDOWN = 25; // seconds between "YOU ARE #1" events
const MAX_EVENTS = 4000; // undrained events beyond this are dropped (oldest first)
// a human that just (re)spawned can't be eaten for this long: a shield fx, so scenes show CF_SHIELD
export const SPAWN_SHIELD = 3;
// a spawn point wants this much room between it and the edge of any cell that could eat a fresh cell
const SPAWN_CLEAR = 300, SPAWN_TRIES = 60, SPAWN_SCAN = 16;
const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));
const dist = (dx: number, dy: number) => Math.sqrt(dx * dx + dy * dy);

// Mass decay per second for a cell above 150 mass, from its owner's total mass. Up to DECAY_SOFT_CAP it is
// the prototype's flat 0.2 %/s; past it the rate climbs with the square of the excess (capped), so a bot
// farming a long-running online room levels off around 10-15k instead of growing without bound (the flat
// rate let one reach millions, cover the map and eat every respawn on arrival). Splitting can't dodge it.
const DECAY_BASE = 0.002, DECAY_SOFT_CAP = 5000, DECAY_SPAN = 2500, DECAY_MAX = 0.1;
export function decayRate(totalMass: number): number {
  const over = totalMass > DECAY_SOFT_CAP ? (totalMass - DECAY_SOFT_CAP) / DECAY_SPAN : 0;
  return Math.min(DECAY_MAX, DECAY_BASE * (1 + over * over));
}
// keeps a cell's centre half its radius inside the walls; one wider than the map sits in the middle
// (clamping to [r/2, WORLD - r/2] alone would push it outside once r > WORLD)
function keepInside(c: Cell): void {
  const e = Math.min(rad(c.m) * 0.5, WORLD / 2);
  c.x = clamp(c.x, e, WORLD - e);
  c.y = clamp(c.y, e, WORLD - e);
}

// ========== ids ==========
// Ids the network layer keys deltas by: unique among live objects of one kind, 1..65535 (fits a u16),
// handed out in a rising sequence that wraps and skips ids still in use, so a freed id is not reused soon.
export class IdPool {
  private next = 1;
  private readonly used = new Set<number>();
  take(): number {
    if (this.used.size >= 65535) throw new Error('IdPool exhausted');
    for (;;) {
      const id = this.next;
      this.next = id >= 65535 ? 1 : id + 1;
      if (!this.used.has(id)) { this.used.add(id); return id; }
    }
  }
  free(id: number): void { this.used.delete(id); }
  has(id: number): boolean { return this.used.has(id); }
  get size(): number { return this.used.size; }
}

// World-internal per-player state on top of the shared Player contract.
export interface SimPlayer extends Player {
  goldenKills: number;
  splitQueued: boolean; // a split pressed since the last step (setInput's split is edge-triggered)
  boostHeld: boolean; // human boost control; p.boosting is the result after the mass check
  eatAcc: number; // mass eaten this step (pellets, enemy cells short of a kill), emitted as one 'eat' event
  aimX: number; aimY: number; // human split/eject aim from setInput (NaN = toward tx/ty)
}

// ========== pellet grid ==========
// Pellets are bucketed into 100-unit squares once per step (a counting sort into one flat array), so
// eating and magnets only test nearby food instead of every pellet on the map.
const GRID = 100, GN = Math.ceil(WORLD / GRID), GCELLS = GN * GN;
const gcol = (v: number) => { const g = (v / GRID) | 0; return g < 0 ? 0 : g >= GN ? GN - 1 : g; };

export class World implements WorldApi {
  now = 0;
  players: SimPlayer[] = [];
  pellets: Pellet[] = [];
  viruses: Virus[] = [];
  powerups: PowerUp[] = [];
  event: ActiveEvent | null = null;
  // bumped whenever a player joins, leaves or changes name/skin (bots respawn as someone new),
  // so hosts know when to resend the roster
  rosterVersion = 0;
  readonly mode: 'solo' | 'online';
  readonly difficulty: DifficultyId;
  readonly rng: Rng;

  private readonly diff: Difficulty;
  private readonly excite: () => boolean;
  private events: SimEvent[] = [];
  private fxOn = false; // power-ups/events latched on once excite() said yes (solo: reset every new life)
  private nextEventAt = 25;
  private nextPowerAt = 3;
  private warming = false;
  private readonly playerIds = new IdPool();
  private readonly cellIds = new IdPool();
  private readonly pelletIds = new IdPool();
  private readonly virusIds = new IdPool();
  private readonly powerIds = new IdPool();
  private readonly gridStart = new Int32Array(GCELLS + 1);
  private readonly gridFill = new Int32Array(GCELLS);
  private gridKey = new Int32Array(4096);
  private readonly gridItems: Pellet[] = [];
  private readonly liveCells: Cell[] = []; // reused every step
  private readonly massScratch: number[] = [];
  private readonly eatenFood: Pellet[] = []; // food eaten this step, respawned after every cell has eaten

  constructor(opts: WorldOptions) {
    this.mode = opts.mode === 'online' ? 'online' : 'solo';
    this.difficulty = opts.difficulty in DIFF ? opts.difficulty : 'normal';
    this.diff = DIFF[this.difficulty];
    this.excite = opts.excite;
    this.rng = opts.rng ?? Math.random;
    for (let i = 0; i < PELLETS; i++) this.pellets.push(this.makePellet());
    for (let i = 0; i < VIRUSES; i++) this.viruses.push(this.makeVirus());
    const names = shuffle(this.rng, [...BOT_NAMES]);
    const bots = clamp(Math.floor(opts.botCount) || 0, 0, 200);
    for (let i = 0; i < bots; i++) this.newPlayer(names[i % names.length], this.pick(SKINS).id, true);
  }

  // ========== helpers ==========
  private rand(a: number, b: number): number { return a + this.rng() * (b - a); }
  private pick<T>(arr: readonly T[]): T { return arr[(this.rng() * arr.length) | 0]; }
  private emit(e: SimEvent): void {
    if (this.events.length >= MAX_EVENTS) this.events.splice(0, MAX_EVENTS >> 1);
    this.events.push(e);
  }
  has(p: Player, k: PowerUpType): boolean {
    const fx = p.fx;
    return fx != null && (fx[k] ?? 0) > this.now;
  }
  // power-ups/events are running (solo: the host's excite() gate has opened for this life)
  get excited(): boolean { return this.fxOn; }
  private byId(id: number): SimPlayer | undefined {
    for (const p of this.players) if (p.id === id) return p;
    return undefined;
  }

  // ========== world ==========
  private makePellet(): Pellet {
    return { id: this.pelletIds.take(), x: this.rand(20, WORLD - 20), y: this.rand(20, WORLD - 20), m: 1, r: 7,
      color: this.pick(PELLET_COLORS), kind: 0, vx: 0, vy: 0 };
  }
  // positions are clamped inside the map so they always fit the wire format
  private addPellet(kind: PelletKind, x: number, y: number, m: number, r: number, color: string, vx: number, vy: number): Pellet {
    const f: Pellet = { id: this.pelletIds.take(), x: clamp(x, 10, WORLD - 10), y: clamp(y, 10, WORLD - 10), m, r, color, kind, vx, vy };
    this.pellets.push(f);
    return f;
  }
  // eaten food reappears elsewhere as a new pellet (new id), so clients see a remove + add, not a jump
  private respawnPellet(f: Pellet): void {
    this.pelletIds.free(f.id);
    f.id = this.pelletIds.take();
    f.x = this.rand(20, WORLD - 20); f.y = this.rand(20, WORLD - 20);
    f.color = this.pick(PELLET_COLORS);
  }
  private makeVirus(x = this.rand(200, WORLD - 200), y = this.rand(200, WORLD - 200)): Virus {
    return { id: this.virusIds.take(), x, y, m: 100, fed: 0, vx: 0, vy: 0 };
  }
  private makeCell(owner: Player, x: number, y: number, m: number): Cell {
    return { id: this.cellIds.take(), owner, x, y, m, vx: 0, vy: 0, merge: 0, r: rad(m) };
  }
  // distance from (x, y) to the edge of the nearest cell that could eat a fresh START_MASS cell (< 0 = inside one)
  private clearance(x: number, y: number): number {
    let best = Infinity;
    for (const p of this.players) for (const c of p.cells) {
      if (c.m <= START_MASS * 1.3) continue;
      const d = dist(c.x - x, c.y - y) - rad(c.m);
      if (d < best) best = d;
    }
    return best;
  }
  // random tries first; on a crowded map, the point farthest from big cells among those tries and a
  // map-wide grid, so a spawn never lands inside a big cell unless big cells cover the whole map
  private safeSpot(): { x: number; y: number } {
    let bx = WORLD / 2, by = WORLD / 2, best = -Infinity;
    const consider = (x: number, y: number): boolean => {
      const s = this.clearance(x, y);
      if (s > best) { best = s; bx = x; by = y; }
      return s >= SPAWN_CLEAR;
    };
    for (let i = 0; i < SPAWN_TRIES; i++) {
      if (consider(this.rand(150, WORLD - 150), this.rand(150, WORLD - 150))) return { x: bx, y: by };
    }
    const step = (WORLD - 300) / SPAWN_SCAN;
    for (let gy = 0; gy < SPAWN_SCAN; gy++) for (let gx = 0; gx < SPAWN_SCAN; gx++) {
      if (consider(150 + (gx + 0.5) * step, 150 + (gy + 0.5) * step)) return { x: bx, y: by };
    }
    return { x: bx, y: by };
  }
  // starts a new life: one START_MASS cell somewhere safe, per-life stats reset; humans get SPAWN_SHIELD
  private spawn(p: SimPlayer): void {
    const s = this.safeSpot();
    this.freeCells(p);
    p.cells.push(this.makeCell(p, s.x, s.y, START_MASS));
    p.tx = s.x; p.ty = s.y;
    p.alive = true; p.fx = p.isBot ? null : { shield: this.now + SPAWN_SHIELD }; p.boosting = false; p.dropAcc = 0;
    p.bestRank = 99; p.peakMass = START_MASS; p.kills = 0; p.goldenKills = 0;
    p.spawnedAt = this.now; p.lastKillerName = 'someone';
    p.combo = { n: 0, until: 0 }; p.milestone = 0; p.wasFirst = false;
    p.splitQueued = false; p.eatAcc = 0;
    const b = p.bot;
    if (b) {
      b.skill = botSkill(this.diff, p.skin, this.rng);
      b.gx = b.ax = s.x; b.gy = b.ay = s.y;
      b.queue.length = 0; b.wantSplit = b.wantBoost = b.wantEject = b.boost = false;
    }
  }
  private newPlayer(name: string, skin: SkinId, isBot: boolean): SimPlayer {
    const p: SimPlayer = {
      id: this.playerIds.take(), name, skin, isBot, cells: [], alive: false, respawnAt: 0,
      tx: 0, ty: 0, boosting: false, dropAcc: 0, trailAt: 0, fx: null, golden: false,
      bestRank: 99, peakMass: START_MASS, kills: 0, spawnedAt: 0, lastKillerName: 'someone',
      combo: { n: 0, until: 0 }, milestone: 0, wasFirst: false, lastFirstToast: -1e9,
      ejectHeld: false, nextEject: 0, bot: isBot ? newBrain() : null,
      goldenKills: 0, splitQueued: false, boostHeld: false, eatAcc: 0, aimX: NaN, aimY: NaN,
    };
    this.spawn(p);
    this.players.push(p);
    this.rosterVersion++;
    return p;
  }
  private freeCells(p: Player): void {
    for (const c of p.cells) this.cellIds.free(c.id);
    p.cells.length = 0;
  }
  // drops cells whose mass went to 0 (merged or eaten), in place
  private pruneCells(p: Player): void {
    const cs = p.cells;
    let j = 0;
    for (let i = 0; i < cs.length; i++) {
      const c = cs[i];
      if (c.m > 0) cs[j++] = c; else this.cellIds.free(c.id);
    }
    cs.length = j;
  }
  // removes matching players in place (outer loops keep their array reference)
  private removePlayers(pred: (p: SimPlayer) => boolean): void {
    const ps = this.players;
    let j = 0, removed = false;
    for (let i = 0; i < ps.length; i++) {
      const p = ps[i];
      if (pred(p)) { this.freeCells(p); this.playerIds.free(p.id); removed = true; continue; }
      ps[j++] = p;
    }
    ps.length = j;
    if (removed) this.rosterVersion++;
  }
  private removeViruses(pred: (v: Virus) => boolean): void {
    const vs = this.viruses;
    let j = 0;
    for (let i = 0; i < vs.length; i++) {
      const v = vs[i];
      if (pred(v)) { this.virusIds.free(v.id); continue; }
      vs[j++] = v;
    }
    vs.length = j;
  }
  // drops eaten pellets and the oldest boost dots beyond the cap, in one pass
  private compactPellets(): void {
    const ps = this.pellets;
    let trailDots = 0;
    for (const f of ps) if (f.kind === 3 && !f.dead) trailDots++;
    let drop = Math.max(0, trailDots - MAX_TRAIL_DOTS), j = 0;
    for (let i = 0; i < ps.length; i++) {
      const f = ps[i];
      if (!f.dead && f.kind === 3 && drop > 0) { drop--; f.dead = true; }
      if (f.dead) { this.pelletIds.free(f.id); continue; }
      ps[j++] = f;
    }
    ps.length = j;
  }
  private rebuildGrid(): void {
    const ps = this.pellets, n = ps.length, start = this.gridStart, fill = this.gridFill, items = this.gridItems;
    if (this.gridKey.length < n) this.gridKey = new Int32Array(n * 2);
    const key = this.gridKey;
    start.fill(0);
    for (let i = 0; i < n; i++) {
      const f = ps[i];
      if (f.dead) { key[i] = -1; continue; }
      const k = gcol(f.y) * GN + gcol(f.x);
      key[i] = k; start[k + 1]++;
    }
    for (let k = 0; k < GCELLS; k++) { fill[k] = start[k]; start[k + 1] += start[k]; }
    items.length = start[GCELLS];
    for (let i = 0; i < n; i++) { const k = key[i]; if (k >= 0) items[fill[k]++] = ps[i]; }
  }

  // ========== players (host API) ==========
  addPlayer(name: string, skin: SkinId, opts?: { isBot?: boolean; profileId?: string }): Player {
    const isBot = !!opts?.isBot;
    const p = this.newPlayer(String(name), SKIN_BY_ID[skin] ? skin : 'blue', isBot);
    if (opts?.profileId) p.profileId = opts.profileId;
    if (!isBot && this.mode === 'solo') this.resetFx();
    else if (!isBot && this.fxOn) this.emit({ t: 'unlock', player: p.id }); // online: late joiners see the gate already open
    return p;
  }
  removePlayer(id: number): void {
    this.removePlayers(p => p.id === id);
  }
  // humans never respawn on their own: the host calls this after the death screen (no-op while alive)
  respawn(id: number): void {
    const p = this.byId(id);
    if (!p || p.alive) return;
    this.spawn(p);
    if (!p.isBot && this.mode === 'solo') this.resetFx();
  }
  setInput(id: number, input: PlayerInput): void {
    const p = this.byId(id);
    if (!p || p.isBot) return;
    if (Number.isFinite(input.tx) && Number.isFinite(input.ty)) {
      p.tx = clamp(input.tx, -WORLD, WORLD * 2); p.ty = clamp(input.ty, -WORLD, WORLD * 2);
    }
    const aim = Number.isFinite(input.aimX) && Number.isFinite(input.aimY);
    p.aimX = aim ? clamp(input.aimX as number, -WORLD, WORLD * 2) : NaN;
    p.aimY = aim ? clamp(input.aimY as number, -WORLD, WORLD * 2) : NaN;
    if (input.split && p.alive) p.splitQueued = true;
    // holding eject fires every EJECT_INTERVAL; re-pressing can't beat that rate
    p.ejectHeld = !!input.eject;
    p.boostHeld = !!input.boost;
  }
  // online: keep humans + bots == n (the Golden Blob doesn't count); dead bots go first, then the smallest
  setBotCount(n: number): void {
    const want = Math.max(0, clamp(Math.floor(n) || 0, 0, 200) - this.players.filter(p => !p.isBot).length);
    const bots = this.players.filter(p => p.isBot && !p.golden);
    if (bots.length > want) {
      bots.sort((a, b) => (a.alive ? 1 : 0) - (b.alive ? 1 : 0) || totalMass(a) - totalMass(b));
      const gone = new Set(bots.slice(0, bots.length - want));
      this.removePlayers(p => gone.has(p));
    } else {
      for (let i = bots.length; i < want; i++) this.newPlayer(this.freshBotName(), this.pick(SKINS).id, true);
    }
  }
  private freshBotName(): string {
    const taken = new Set(this.players.map(p => p.name));
    const free = BOT_NAMES.filter(n => !taken.has(n));
    return this.pick(free.length ? free : BOT_NAMES);
  }
  leaderboard(): { id: number; name: string; mass: number; isBot: boolean }[] {
    const out: { id: number; name: string; mass: number; isBot: boolean }[] = [];
    for (const p of this.players) if (p.alive) out.push({ id: p.id, name: p.name, mass: totalMass(p), isBot: p.isBot });
    return out.sort((a, b) => b.mass - a.mass);
  }
  drainEvents(): SimEvent[] {
    const e = this.events;
    this.events = [];
    return e;
  }

  // ========== actions ==========
  private split(p: SimPlayer): void {
    const n0 = p.cells.length, now = this.now, [gx, gy] = this.aimOf(p);
    let did = false;
    for (let i = 0; i < n0; i++) {
      const c = p.cells[i];
      if (p.cells.length >= MAX_CELLS || c.m < 36) continue;
      const dx = gx - c.x, dy = gy - c.y, d = dist(dx, dy) || 1;
      const half = c.m / 2;
      c.m = half; c.r = rad(half);
      const n = this.makeCell(p, c.x, c.y, half);
      const v = 1150 + rad(half) * 3;
      n.vx = dx / d * v; n.vy = dy / d * v;
      c.merge = n.merge = now + 10 + half * 0.02;
      p.cells.push(n);
      did = true;
    }
    if (did && !p.isBot) this.emit({ t: 'split', player: p.id });
  }
  private eject(p: SimPlayer): void {
    const color = SKIN_BY_ID[p.skin].color, [gx, gy] = this.aimOf(p);
    let did = false;
    for (const c of p.cells) {
      if (c.m < 35) continue;
      const dx = gx - c.x, dy = gy - c.y, d = dist(dx, dy) || 1;
      const ux = dx / d + this.rand(-0.08, 0.08), uy = dy / d + this.rand(-0.08, 0.08);
      c.m -= 16;
      const r = rad(c.m);
      this.addPellet(1, c.x + ux * r, c.y + uy * r, 13, 16, color, ux * 1100, uy * 1100);
      did = true;
    }
    if (did && !p.isBot) this.emit({ t: 'eject', player: p.id });
  }
  // where split/eject fire: the setInput aim when there is one (prototype aimed()), else the steering target
  private aimOf(p: SimPlayer): [number, number] {
    return Number.isFinite(p.aimX) && Number.isFinite(p.aimY) ? [p.aimX, p.aimY] : [p.tx, p.ty];
  }
  private popOnVirus(c: Cell, v: Virus): void {
    const p = c.owner, now = this.now;
    c.m += v.m * 0.4;
    const room = MAX_CELLS - p.cells.length;
    const pieces = Math.min(room, Math.floor(c.m / 24), 10);
    if (pieces <= 0) return;
    const share = c.m / (pieces + 1);
    c.m = share;
    for (let i = 0; i < pieces; i++) {
      const a = (i / pieces) * Math.PI * 2;
      const n = this.makeCell(p, c.x, c.y, share);
      n.vx = Math.cos(a) * 750; n.vy = Math.sin(a) * 750;
      n.merge = now + 12 + share * 0.02;
      p.cells.push(n);
    }
    c.merge = now + 12 + share * 0.02;
  }
  // boosting drops mass behind you as edible dots; the afterimages are the renderer's job
  private boostDrain(p: SimPlayer, dt: number): void {
    const rate = 8 + totalMass(p) * 0.012; // dots (= mass) per second
    p.dropAcc += rate * dt;
    const color = SKIN_BY_ID[p.skin].color;
    while (p.dropAcc >= 1) {
      p.dropAcc -= 1;
      const c = biggestCell(p);
      if (!c || c.m - BOOST_DOT_MASS < 12) return;
      c.m -= BOOST_DOT_MASS;
      const dx = p.tx - c.x, dy = p.ty - c.y, d = dist(dx, dy) || 1;
      const r = rad(c.m) + 12, side = this.rand(-0.5, 0.5);
      const f = this.addPellet(3, c.x - dx / d * r - dy / d * side * r * 0.6, c.y - dy / d * r + dx / d * side * r * 0.6,
        BOOST_DOT_MASS, 6, color, 0, 0);
      f.born = this.now;
    }
  }

  // ========== power-ups, events, celebrations ==========
  // the event and its spawns; public so hosts and tests can trigger one (needs no excite gate)
  startEvent(type: EventType): void {
    if (this.event) this.endEvent(false);
    const now = this.now, def = EVENTS[type];
    this.event = { type, start: now, until: now + def.dur };
    this.emit({ t: 'eventStart', type });
    if (type === 'rush') for (let i = 0; i < 350; i++) {
      const f = this.addPellet(2, this.rand(20, WORLD - 20), this.rand(20, WORLD - 20), 3, 9, RUSH_COLOR, 0, 0);
      f.ph = this.rand(0, 6);
    }
    if (type === 'swarm') for (let i = 0; i < 16; i++) { const v = this.makeVirus(); v.temp = true; this.viruses.push(v); }
    if (type === 'gold') {
      const g = this.newPlayer('Golden Blob', 'gold', true);
      g.golden = true;
      const c = g.cells[0];
      c.m = 260; c.r = rad(260);
      if (g.bot) g.bot.skill = { ...GOLDEN_SKILL };
    }
  }
  // silent: a solo reset, not something to announce
  private endEvent(silent: boolean): void {
    const e = this.event;
    if (!e) return;
    let goldenEscaped: boolean | undefined;
    if (e.type === 'rush') { for (const f of this.pellets) if (f.kind === 2) f.dead = true; this.compactPellets(); }
    if (e.type === 'swarm') this.removeViruses(v => !!v.temp);
    if (e.type === 'gold') {
      const g = this.players.find(p => p.golden);
      goldenEscaped = !!g && g.alive && g.cells.length > 0;
      this.removePlayers(p => p.golden);
    }
    this.event = null;
    this.nextEventAt = this.now + this.rand(45, 75);
    if (!silent) this.emit(e.type === 'gold' ? { t: 'eventEnd', type: e.type, goldenEscaped } : { t: 'eventEnd', type: e.type });
  }
  // solo: a new life starts with no power-ups or event and the excite gate closed, like the prototype's startGame
  private resetFx(): void {
    this.endEvent(true);
    for (const pu of this.powerups) this.powerIds.free(pu.id);
    this.powerups.length = 0;
    this.fxOn = false;
    this.nextEventAt = this.now + 25; this.nextPowerAt = this.now + 3;
  }
  private onPickup(p: SimPlayer, pu: PowerUp): void {
    const def = POWERUPS[pu.type];
    const fx = p.fx ?? (p.fx = {});
    fx[pu.type] = Math.max(fx[pu.type] ?? 0, this.now) + def.dur;
    if (!p.isBot) this.emit({ t: 'pickup', player: p.id, type: pu.type, x: pu.x, y: pu.y });
  }
  private onKill(killer: SimPlayer, victim: SimPlayer): void {
    const now = this.now, combo = killer.combo;
    combo.n = now < combo.until ? combo.n + 1 : 1;
    combo.until = now + 4;
    killer.kills++;
    if (victim.golden) killer.goldenKills++;
    // bots' kills only matter when the Golden Blob is the victim (announced to everyone)
    if (!killer.isBot || victim.golden) {
      this.emit({ t: 'kill', killer: killer.id, victim: victim.id, victimName: victim.name, combo: combo.n, golden: victim.golden });
    }
  }

  // Power-ups and events only run while a human is playing (the prototype's `running`) and not during
  // warm-up; spawning them also needs the host's excite() gate.
  private updateFx(dt: number): void {
    // an event ends on time whatever else is going on (no human alive, warm-up, excite gate closed)
    if (this.event && this.now > this.event.until) this.endEvent(false);
    if (this.warming) return;
    const now = this.now, players = this.players;
    let humanAlive = false;
    for (const p of players) if (!p.isBot && p.alive) { humanAlive = true; break; }
    if (!humanAlive) return;
    if (!this.fxOn && this.excite()) {
      this.fxOn = true;
      this.nextPowerAt = now + 1; this.nextEventAt = now + 20;
      for (const p of players) if (!p.isBot) this.emit({ t: 'unlock', player: p.id });
    }
    if (this.fxOn) {
      if (now > this.nextPowerAt && this.powerups.length < 7) {
        this.powerups.push({ id: this.powerIds.take(), type: this.pick(POWERUP_TYPES), x: this.rand(200, WORLD - 200),
          y: this.rand(200, WORLD - 200), ph: this.rand(0, 6), born: now });
        this.nextPowerAt = now + this.rand(4, 8);
      }
      if (!this.event && now > this.nextEventAt) this.startEvent(this.pick(EVENT_TYPES));
    }
    // pickups and magnet pull for every player
    const pus = this.powerups, start = this.gridStart, items = this.gridItems;
    for (const p of players) {
      if (!p.alive) continue;
      const magnet = this.has(p, 'magnet');
      for (const c of p.cells) {
        const r = rad(c.m), pr = r + 18;
        for (let i = pus.length - 1; i >= 0; i--) {
          const pu = pus[i], dx = pu.x - c.x, dy = pu.y - c.y;
          if (dx * dx + dy * dy < pr * pr) { this.onPickup(p, pu); this.powerIds.free(pu.id); pus.splice(i, 1); }
        }
        if (!magnet) continue;
        const reach = r + 320, k1 = 420 * dt, k0 = 60 * dt;
        const gx0 = gcol(c.x - reach), gx1 = gcol(c.x + reach), gy1 = gcol(c.y + reach);
        for (let gy = gcol(c.y - reach); gy <= gy1; gy++) for (let gx = gx0; gx <= gx1; gx++) {
          const k = gy * GN + gx;
          for (let i = start[k], e = start[k + 1]; i < e; i++) {
            const f = items[i];
            if (f.dead) continue;
            const dx = c.x - f.x, dy = c.y - f.y, d = dist(dx, dy);
            if (d < reach && d > 1) { const s = k1 * (1 - d / reach) + k0; f.x += dx / d * s; f.y += dy / d * s; }
          }
        }
      }
    }
  }
  // #1 banner (with cooldown), mass milestones, best rank and peak mass for every living human
  private humanStats(): void {
    const now = this.now, players = this.players, mass = this.massScratch;
    mass.length = players.length;
    let humans = 0;
    for (let i = 0; i < players.length; i++) {
      const p = players[i];
      mass[i] = p.alive ? totalMass(p) : -1;
      if (!p.isBot && p.alive) humans++;
    }
    if (!humans) return;
    for (let i = 0; i < players.length; i++) {
      const p = players[i];
      if (p.isBot || !p.alive) continue;
      const m = mass[i];
      let rank = 0;
      for (let j = 0; j < players.length; j++) if (j !== i && mass[j] > m) rank++;
      if (!this.warming) {
        if (rank === 0 && !p.wasFirst && m > 100 && now - p.lastFirstToast > FIRST_PLACE_COOLDOWN) {
          p.lastFirstToast = now;
          this.emit({ t: 'first', player: p.id });
        }
        p.wasFirst = rank === 0;
        while (p.milestone < MILESTONES.length && m >= MILESTONES[p.milestone]) {
          this.emit({ t: 'milestone', player: p.id, mass: MILESTONES[p.milestone] });
          p.milestone++;
        }
      }
      if (m > p.peakMass) p.peakMass = m;
      if (rank < p.bestRank) p.bestRank = rank;
    }
  }

  // ========== simulation ==========
  step(dt: number): void {
    if (!(dt > 0)) return;
    if (dt > 0.05) dt = 0.05;
    this.now += dt;
    this.update(dt);
  }
  // simulate ahead (power-ups and events stay off) so bots have grown when the player joins
  warmUp(seconds: number): void {
    const n = clamp(Math.round(seconds * 30) || 0, 0, 30 * 600);
    this.warming = true;
    try {
      for (let i = 0; i < n; i++) { this.now += 1 / 30; this.update(1 / 30); }
    } finally {
      this.warming = false;
    }
  }

  private update(dt: number): void {
    const now = this.now, players = this.players;
    // humans: a queued split fires once; held eject fires at a steady rate instead of relying on key repeat
    for (const p of players) {
      if (p.isBot) continue;
      if (!p.alive) { p.splitQueued = false; continue; }
      if (p.splitQueued) { p.splitQueued = false; this.split(p); }
      if (p.ejectHeld && now >= p.nextEject) { this.eject(p); p.nextEject = now + EJECT_INTERVAL; }
    }
    const turn = 1 - Math.exp(-5 * dt), decay = Math.exp(-5 * dt);
    for (const p of players) {
      if (!p.alive) {
        if (p.isBot && !p.golden && now > p.respawnAt) {
          p.name = this.pick(BOT_NAMES); p.skin = this.pick(SKINS).id;
          this.spawn(p);
          this.rosterVersion++;
        }
        continue;
      }
      const brain = p.bot;
      if (brain) {
        // decide now, but act only after a human-like reaction delay
        if (now > brain.nextThink) {
          botThink(this, p);
          brain.queue.push({ at: now + brain.skill.react * this.rand(0.7, 1.5), x: brain.gx, y: brain.gy,
            split: brain.wantSplit, boost: brain.wantBoost, eject: brain.wantEject });
          brain.wantSplit = false;
        }
        while (brain.queue.length && brain.queue[0].at <= now) {
          const q = brain.queue.shift()!;
          brain.ax = q.x; brain.ay = q.y;
          brain.boost = q.boost;
          if (q.split) { p.tx = q.x; p.ty = q.y; this.split(p); }
          if (q.eject) { p.tx = q.x; p.ty = q.y; this.eject(p); }
        }
        // turn gradually toward the aim point instead of snapping like a mouse teleport
        p.tx += (brain.ax - p.tx) * turn; p.ty += (brain.ay - p.ty) * turn;
      }
      p.boosting = (brain ? brain.boost : p.boostHeld) && totalMass(p) > BOOST_MIN_MASS;
      if (p.boosting) this.boostDrain(p, dt);
      const fast = (this.has(p, 'speed') ? 1.6 : 1) * (p.boosting ? BOOST_SPEED : 1);
      const shrink = decayRate(totalMass(p)) * dt;
      for (const c of p.cells) {
        const r = rad(c.m);
        const dx = p.tx - c.x, dy = p.ty - c.y, d = dist(dx, dy) || 1;
        const sp = speedOf(c.m) * fast * Math.min(1, d / (r * 0.6 + 20));
        c.x += (dx / d * sp + c.vx) * dt;
        c.y += (dy / d * sp + c.vy) * dt;
        c.vx *= decay; c.vy *= decay;
        if (c.m > 150) c.m -= c.m * shrink;
        keepInside(c);
      }
      // own cells: push apart until merge time, then merge
      const cs = p.cells;
      for (let i = 0; i < cs.length; i++) for (let j = i + 1; j < cs.length; j++) {
        const a = cs[i], b = cs[j];
        if (a.m <= 0 || b.m <= 0) continue;
        const dx = b.x - a.x, dy = b.y - a.y, d = dist(dx, dy) || 0.01;
        const ra = rad(a.m), rb = rad(b.m);
        if (d >= ra + rb) continue;
        if (now > a.merge && now > b.merge) {
          if (d < Math.max(ra, rb) * 0.75) {
            const keep = a.m >= b.m ? a : b, gone = keep === a ? b : a;
            keep.m += gone.m; gone.m = 0;
          }
        } else if (a.vx * a.vx + a.vy * a.vy < 90000 && b.vx * b.vx + b.vy * b.vy < 90000) {
          const push = (ra + rb - d) / 2, ux = dx / d, uy = dy / d;
          const wa = b.m / (a.m + b.m), wb = a.m / (a.m + b.m);
          a.x -= ux * push * wa * 2; a.y -= uy * push * wa * 2;
          b.x += ux * push * wb * 2; b.y += uy * push * wb * 2;
        }
      }
      this.pruneCells(p);
      for (const c of cs) keepInside(c); // pushes and merges can shove a cell past the wall
    }

    // moving pellets (ejected mass) and viruses
    const slow = Math.exp(-4 * dt);
    for (const f of this.pellets) if (f.vx || f.vy) {
      f.x = clamp(f.x + f.vx * dt, 10, WORLD - 10); f.y = clamp(f.y + f.vy * dt, 10, WORLD - 10);
      f.vx *= slow; f.vy *= slow;
      if (Math.abs(f.vx) + Math.abs(f.vy) < 5) f.vx = f.vy = 0;
    }
    for (const v of this.viruses) if (v.vx || v.vy) {
      v.x = clamp(v.x + v.vx * dt, 50, WORLD - 50); v.y = clamp(v.y + v.vy * dt, 50, WORLD - 50);
      v.vx *= slow; v.vy *= slow;
      if (Math.abs(v.vx) + Math.abs(v.vy) < 5) v.vx = v.vy = 0;
    }

    const cells = this.liveCells;
    cells.length = 0;
    for (const p of players) if (p.alive) for (const c of p.cells) { c.r = rad(c.m); cells.push(c); }

    // eat pellets (only the ones in nearby grid squares). An eaten pellet is marked dead at once so no
    // other cell gets it this step; food comes back to life elsewhere only once every cell has eaten.
    this.rebuildGrid();
    const start = this.gridStart, items = this.gridItems, eatenFood = this.eatenFood;
    eatenFood.length = 0;
    for (const c of cells) {
      const r = c.r, r2 = r * r, owner = c.owner as SimPlayer;
      const gx0 = gcol(c.x - r), gx1 = gcol(c.x + r), gy1 = gcol(c.y + r);
      for (let gy = gcol(c.y - r); gy <= gy1; gy++) for (let gx = gx0; gx <= gx1; gx++) {
        const k = gy * GN + gx;
        for (let i = start[k], e = start[k + 1]; i < e; i++) {
          const f = items[i];
          if (f.dead) continue;
          const dx = f.x - c.x, dy = f.y - c.y;
          if (dx * dx + dy * dy >= r2) continue;
          if (f.kind === 1 && f.vx * f.vx + f.vy * f.vy > 250000) continue; // just fired
          c.m += f.m; owner.eatAcc += f.m;
          f.dead = true;
          if (f.kind === 0) eatenFood.push(f);
        }
      }
    }
    for (const f of eatenFood) { f.dead = false; this.respawnPellet(f); }
    eatenFood.length = 0;
    // ejected mass feeding viruses: the 7th shot fires a new virus the way the mass was flying
    for (const f of this.pellets) {
      if (f.kind !== 1 || f.dead) continue;
      for (const v of this.viruses) {
        if (dist(f.x - v.x, f.y - v.y) < rad(v.m)) {
          v.fed++; v.m += 6;
          if (v.fed >= 7 && this.viruses.length < VIRUSES * 2) {
            const sp = dist(f.vx, f.vy) || 1;
            const nv = this.makeVirus(v.x, v.y);
            nv.vx = (f.vx || 1) / sp * 900; nv.vy = (f.vy || 0) / sp * 900;
            this.viruses.push(nv);
            v.fed = 0; v.m = 100;
          }
          f.dead = true;
          break;
        }
      }
    }
    // cells eat cells
    cells.sort((a, b) => b.m - a.m);
    for (let i = 0; i < cells.length; i++) {
      const a = cells[i];
      if (a.m <= 0) continue;
      for (let j = i + 1; j < cells.length; j++) {
        const b = cells[j];
        if (b.m <= 0 || b.owner === a.owner) continue;
        if (a.m < b.m * 1.25 || this.has(b.owner, 'shield')) continue;
        if (dist(a.x - b.x, a.y - b.y) < a.r - b.r * 0.35) {
          const gained = b.m;
          a.m += gained; b.m = 0;
          const victim = b.owner as SimPlayer, killer = a.owner as SimPlayer;
          victim.lastKillerName = killer.name;
          // the last cell is a kill; any other enemy cell counts as a (big) bite
          if (victim.cells.every(x => x.m <= 0)) this.onKill(killer, victim);
          else killer.eatAcc += gained;
        }
      }
    }
    // one 'eat' per human per step: food, ejected mass, boost dots and enemy cells that weren't a kill
    for (const p of players) if (p.eatAcc > 0) {
      if (!p.isBot) this.emit({ t: 'eat', player: p.id, mass: p.eatAcc });
      p.eatAcc = 0;
    }
    // viruses pop big cells
    const vs = this.viruses;
    for (let i = vs.length - 1; i >= 0; i--) {
      const v = vs[i], vr = rad(v.m);
      for (const c of cells) {
        if (c.m < 133 || this.has(c.owner, 'ghost')) continue;
        if (dist(c.x - v.x, c.y - v.y) < c.r - vr * 0.3) {
          this.popOnVirus(c, v);
          if (!c.owner.isBot) this.emit({ t: 'virusPop', player: c.owner.id });
          this.virusIds.free(v.id);
          vs.splice(i, 1);
          break;
        }
      }
    }
    while (vs.length < VIRUSES) vs.push(this.makeVirus());

    // deaths: bots come back by themselves after 2-5 s, humans wait for the host's respawn()
    for (const p of players) {
      if (!p.alive) continue;
      this.pruneCells(p);
      if (p.cells.length) continue;
      p.alive = false;
      p.respawnAt = now + this.rand(2, 5);
      p.fx = null; p.boosting = false;
      if (p.bot) p.bot.queue.length = 0;
      if (!p.isBot) {
        this.emit({ t: 'death', player: p.id, killerName: p.lastKillerName, bestRank: p.bestRank, peakMass: p.peakMass,
          kills: p.kills, aliveSec: now - p.spawnedAt, goldenKills: p.goldenKills });
      }
    }
    if (this.event && this.event.type === 'gold' && !players.some(p => p.golden && p.alive)) this.endEvent(false);
    if (players.some(p => p.golden && !p.alive)) this.removePlayers(p => p.golden && !p.alive);

    this.updateFx(dt);
    this.humanStats();
    this.compactPellets();
    for (const p of players) for (const c of p.cells) c.r = rad(c.m);
  }
}
